import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { trackPromise } from "react-promise-tracker";

import {
	Box,
	Button,
	createListCollection,
	Field,
	Input,
	MetreaTooltip,
	MetreaSelectContent as SelectContent,
	MetreaSelectItem as SelectItem,
	MetreaSelectLabel as SelectLabel,
	MetreaSelectRoot as SelectRoot,
	MetreaSelectTrigger as SelectTrigger,
	MetreaSelectValueText as SelectValueText,
	Portal,
	Table,
	Text,
} from "@metrea-ui/v2";
import { EnhancedTableHead } from "src/components/UI/common/EnhancedTableParts";
import { getShortDateTimeString } from "src/utils/formatUtils";
import { debugLog } from "src/utils/debugUtils";

const initialFilters = {
	keyword: "",
	from: "",
	to: "",
	page: "",
	actingAs: "",
	loginFilter: "both",
	event: "",
	ip: "",
};

const loginFilterOptions = [
	// "Login" here means authenticated/session-bound activity, not only the literal userLogin event.
	{ id: "both", name: "Both" },
	{ id: "login", name: "Login Only" },
	{ id: "non-login", name: "Non-Login Only" },
];

const sortOptions = [
	{ id: "log_dt:desc", label: "Timestamp (Newest)" },
	{ id: "log_dt:asc", label: "Timestamp (Oldest)" },
	{ id: "user_email:asc", label: "User (A-Z)" },
	{ id: "user_email:desc", label: "User (Z-A)" },
	{ id: "as_user_email:asc", label: "Acting As (A-Z)" },
	{ id: "as_user_email:desc", label: "Acting As (Z-A)" },
	{ id: "page:asc", label: "Module / Page (A-Z)" },
	{ id: "page:desc", label: "Module / Page (Z-A)" },
	{ id: "event:asc", label: "Action (A-Z)" },
	{ id: "event:desc", label: "Action (Z-A)" },
	{ id: "ip:asc", label: "IP (A-Z)" },
	{ id: "ip:desc", label: "IP (Z-A)" },
];

const rowsPerPageOptions = [
	{ id: "10", label: "10 rows" },
	{ id: "25", label: "25 rows" },
	{ id: "50", label: "50 rows" },
	{ id: "100", label: "100 rows" },
];

export const formatDetails = (details) => {
	if (details === null || details === undefined || details === "") return "-";
	if (typeof details !== "string") return JSON.stringify(details);

	try {
		const parsed = JSON.parse(details);
		return typeof parsed === "string" ? parsed : JSON.stringify(parsed);
	} catch (_error) {
		return details;
	}
};

const formatCount = (value) => Number(value || 0).toLocaleString();

const getSelectLabel = (item) =>
	item?.label ??
	item?.name ??
	item?.display_name ??
	item?.title ??
	item?.text ??
	String(item?.id ?? "");

const findSelectOption = (items, rawValue) =>
	(items ?? []).find((item) => String(item?.id) === String(rawValue)) ??
	(items ?? []).find((item) => String(item?.value) === String(rawValue)) ??
	null;

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	if (size === "xs" || size === "2xs") return "xs";
	return size || "sm";
};

const getSelectValue = (item) => String(item?.id ?? item?.value ?? "");

const buildSelectCollectionItems = ({ items, includeEmpty = false, placeholder = "" }) => [
	...(includeEmpty ? [{ label: placeholder, value: "" }] : []),
	...items.map((item) => ({
		label: getSelectLabel(item),
		value: getSelectValue(item),
	})),
];

// Keep these simple filter controls local during T-01039; they preserve the
// plain onChange(event) contract without turning back into a shared wrapper.
const renderTextFilterField = ({ label, name, value, onChange, placeholder, type = "text" }) => (
	<Field.Root style={{ width: "100%" }}>
		<Field.Label htmlFor={name}>{label}</Field.Label>
		<Input
			id={name}
			name={name}
			type={type}
			value={value ?? ""}
			onChange={onChange}
			placeholder={placeholder}
			size="xs"
			variant="subtle"
			style={{
				width: "100%",
				fontSize: "0.75rem",
			}}
		/>
	</Field.Root>
);

const formatUserLabel = (row, asUser = false) => {
	const fullName = asUser ? row?.as_user_full_name : row?.user_full_name;
	const email = asUser ? row?.as_user_email : row?.user_email;
	const userId = asUser ? row?.as_user_id : row?.user_id;

	if (fullName && email) return `${fullName} (${email})`;
	if (email) return email;
	if (fullName) return fullName;
	if (userId) return `User #${userId}`;
	return "-";
};

const truncateText = (value, maxLength = 80) => {
	const text = String(value || "");
	if (text.length <= maxLength) return text;
	return `${text.slice(0, Math.max(maxLength - 3, 0))}...`;
};

const AutoTooltipText = ({ children }) => {
	const ref = useRef(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const measure = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		setIsTruncated(el.scrollWidth - el.clientWidth > 0.5);
	}, []);

	useLayoutEffect(() => {
		measure();
		const el = ref.current;
		if (!el || typeof ResizeObserver === "undefined") return;
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [measure, children]);

	const valueBox = (
		<Box
			ref={ref}
			flex="1 1 auto"
			minW={0}
			overflow="hidden"
			textOverflow="ellipsis"
			whiteSpace="nowrap"
		>
			{children}
		</Box>
	);

	if (!isTruncated) return valueBox;

	return (
		<MetreaTooltip
			content={
				<Box whiteSpace="pre-line" wordBreak="break-word" maxW={480}>
					{String(children)}
				</Box>
			}
			positioning={{ placement: "top" }}
		>
			{valueBox}
		</MetreaTooltip>
	);
};

const renderTruncatedCell = (value, maxWidth = "280px") => {
	const text = String(value ?? "");

	return (
		<Box display="block" maxWidth={maxWidth} minWidth={0}>
			{/* Match CardList behavior: rely on rendered overflow instead of character count guesses. */}
			<AutoTooltipText>{text}</AutoTooltipText>
		</Box>
	);
};

const renderDetailsPreview = (details) => {
	return renderTruncatedCell(formatDetails(details), "280px");
};

const headCells = [
	{ id: "log_dt", label: "Timestamp", numeric: false, disablePadding: false },
	{ id: "user_email", label: "User", numeric: false, disablePadding: false },
	{ id: "as_user_email", label: "Acting As", numeric: false, disablePadding: false },
	{ id: "page", label: "Module / Page", numeric: false, disablePadding: false },
	{ id: "event", label: "Action", numeric: false, disablePadding: false },
	{ id: "ip", label: "IP", numeric: false, disablePadding: false },
	{ id: "details", label: "Details", numeric: false, disablePadding: false, disableSort: true },
];

const UserActivityResults = memo(function UserActivityResults({
	items,
	order,
	orderBy,
	onRequestSort,
	// Keep the CardList columns config available here so we can re-enable that view
	// without rebuilding its tooltip/sort metadata from scratch.
	columns,
}) {
	return (
		<>
			{/* <Box>
				<CardList
					rows={items}
					keyField="id"
					columns={columns}
					showLabels
					density="compact"
					responsive={{
						mode: "auto",
						maxRows: 6,
						minColWidth: 110,
						gap: 12,
					}}
					showVerticalDividers
					emptyState={<Box p={4}>No activities found.</Box>}
				/>
			</Box> */}

			<Box
				width="100%"
				marginTop="8px"
				marginBottom="12px"
				bg="white"
				boxShadow="sm"
				rounded="md"
			>
				<Box overflowX="auto">
					<Table.Root
						size="sm"
						variant="outline"
						aria-labelledby="user-activity-table"
						style={{ fontSize: "0.75rem" }}
					>
						<EnhancedTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={onRequestSort}
							rowCount={items.length}
							headCells={headCells}
						/>
						<Table.Body>
							{items.map((row) => (
								<Table.Row key={row.id}>
									<Table.Cell>{renderTruncatedCell(getShortDateTimeString(row.log_dt), "150px")}</Table.Cell>
									<Table.Cell>{renderTruncatedCell(formatUserLabel(row), "220px")}</Table.Cell>
									<Table.Cell>{renderTruncatedCell(formatUserLabel(row, true), "220px")}</Table.Cell>
									<Table.Cell>{renderTruncatedCell(row.page || "-", "150px")}</Table.Cell>
									<Table.Cell>{renderTruncatedCell(row.event || "-", "220px")}</Table.Cell>
									<Table.Cell>{renderTruncatedCell(row.ip || "-", "150px")}</Table.Cell>
									<Table.Cell>{renderDetailsPreview(row.details)}</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table.Root>
				</Box>
			</Box>
		</>
	);
});

const UserActivitiesList = ({
	items = [],
	meta = {},
	loading = false,
	onApplyFilters,
	onClearFilters,
	onNextBatch,
	onPrevBatch,
	onPageSizeChange,
	onSortChange,
}) => {
	const [filters, setFilters] = useState(initialFilters);
	const [order, setOrder] = useState("desc");
	const [orderBy, setOrderBy] = useState("log_dt");

	const pageOptions = useMemo(() => {
		const options = Array.isArray(meta?.pageOptions) ? meta.pageOptions : [];
		return options.map((option) => ({
			id: String(option.id),
			name: option.name,
		}));
	}, [meta]);

	const actingAsOptions = useMemo(() => {
		const options = Array.isArray(meta?.actingAsOptions) ? meta.actingAsOptions : [];
		// Backend can prepend a synthetic "No Acting As" option alongside real acting-user rows.
		return options.map((option) => ({
			id: String(option.id),
			name: option.name,
		}));
	}, [meta]);

	useEffect(() => {
		setFilters({
			keyword: meta?.filters?.keyword || "",
			from: meta?.filters?.from || "",
			to: meta?.filters?.to || "",
			page: meta?.filters?.page || "",
			actingAs: meta?.filters?.actingAs || "",
			loginFilter: meta?.filters?.loginFilter || "both",
			event: meta?.filters?.event || "",
			ip: meta?.filters?.ip || "",
		});
		setOrder(meta?.filters?.sortOrder === "asc" ? "asc" : "desc");
		setOrderBy(meta?.filters?.sortBy || "log_dt");
	}, [meta]);

	const handleRequestSort = useCallback((_event, property) => {
		const isAsc = orderBy === property && order === "asc";
		const nextOrder = isAsc ? "desc" : "asc";
		setOrder(nextOrder);
		setOrderBy(property);
		trackPromise(onSortChange?.({ sortBy: property, sortOrder: nextOrder }));
	}, [onSortChange, order, orderBy]);

	const handleSortOptionChange = (option) => {
		const [nextOrderBy, nextOrder] = String(option?.id || "log_dt:desc").split(":");
		const resolvedSortBy = nextOrderBy || "log_dt";
		const resolvedSortOrder = nextOrder === "asc" ? "asc" : "desc";
		setOrderBy(resolvedSortBy);
		setOrder(resolvedSortOrder);
		// Sorting is server-driven for User Activity so Previous/Next keep the same global order.
		trackPromise(onSortChange?.({ sortBy: resolvedSortBy, sortOrder: resolvedSortOrder }));
	};

	const columns = useMemo(
		() => [
			// Preserve the CardList column definition even while the table view is active.
			// It keeps the old layout path easy to restore and avoids duplicating labels/tooltip metadata.
			{
				key: "log_dt",
				title: "Timestamp",
				minW: 160,
				render: (row) => truncateText(getShortDateTimeString(row.log_dt), 24),
			},
			{
				key: "user_email",
				title: "User",
				minW: 220,
				render: (row) => truncateText(formatUserLabel(row), 48),
				tooltip: "overflow",
				tooltipText: (row) => formatUserLabel(row),
			},
			{
				key: "as_user_email",
				title: "Acting As",
				minW: 220,
				render: (row) => truncateText(formatUserLabel(row, true), 48),
				tooltip: "overflow",
				tooltipText: (row) => formatUserLabel(row, true),
			},
			{
				key: "page",
				title: "Module / Page",
				minW: 120,
				render: (row) => truncateText(row.page || "-", 24),
			},
			{
				key: "event",
				title: "Action",
				minW: 180,
				render: (row) => truncateText(row.event || "-", 36),
			},
			{
				key: "ip",
				title: "IP",
				minW: 120,
				render: (row) => truncateText(row.ip || "-", 24),
			},
			{
				key: "details",
				title: "Details",
				minW: 220,
				render: (row) => truncateText(formatDetails(row.details), 80),
				tooltip: "overflow",
				tooltipText: (row) => formatDetails(row.details),
			},
		],
		[],
	);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const handlePageChange = (_name, selectedId) => {
		setFilters((prev) => ({ ...prev, page: selectedId || "" }));
	};

	const handleActingAsChange = (_name, selectedId) => {
		setFilters((prev) => ({ ...prev, actingAs: selectedId || "" }));
	};

	const handleLoginFilterChange = (_name, selectedId) => {
		setFilters((prev) => ({ ...prev, loginFilter: selectedId || "both" }));
	};

	const applyFilters = () => {
		debugLog(3, "[UserActivitiesList][ACTION] applyFilters", { filters });
		trackPromise(onApplyFilters?.(filters));
	};

	const clearFilters = () => {
		debugLog(3, "[UserActivitiesList][ACTION] clearFilters", {});
		setFilters(initialFilters);
		trackPromise(onClearFilters?.());
	};

	const handleRowsPerPageChange = (event) => {
		// Rows-per-page now drives the server limit so the screen only has one pagination model.
		onPageSizeChange?.(Number(event.target.value) || 100);
	};

	const total = Number(meta?.total) || items.length;
	const offset = Number(meta?.offset) || 0;
	const currentLimit = Number(meta?.limit) || 100;
	const batchStart = total === 0 ? 0 : offset + 1;
	const batchEnd = offset + items.length;
	const hasMore = Boolean(meta?.hasMore);
	const currentSortId = `${orderBy}:${order}`;
	const sortCollection = useMemo(
		() => createListCollection({ items: buildSelectCollectionItems({ items: sortOptions }) }),
		[]
	);
	const rowsPerPageCollection = useMemo(
		() => createListCollection({ items: buildSelectCollectionItems({ items: rowsPerPageOptions }) }),
		[]
	);
	const actingAsCollection = useMemo(
		() =>
			createListCollection({
				items: buildSelectCollectionItems({
					items: actingAsOptions,
					includeEmpty: true,
					placeholder: "All acting users",
				}),
			}),
		[actingAsOptions]
	);
	const pageCollection = useMemo(
		() =>
			createListCollection({
				items: buildSelectCollectionItems({
					items: pageOptions,
					includeEmpty: true,
					placeholder: "All modules / pages",
				}),
			}),
		[pageOptions]
	);
	const loginCollection = useMemo(
		() => createListCollection({ items: buildSelectCollectionItems({ items: loginFilterOptions }) }),
		[]
	);

	// Keep the existing toolbar handlers stable while using the direct select path:
	// callers still receive the matched option object instead of a raw selected value string.
	const renderSelect = ({
		label,
		name,
		collection,
		value,
		placeholder,
		size = "xs",
		width = "100%",
		isCompact = false,
		onValueSelect,
	}) => {
		const selectControl = (
			<SelectRoot
				data-raven-compact-select={isCompact ? "true" : undefined}
				variant="outline"
				name={name}
				collection={collection}
				size={sizeToSelectSize(size)}
				width={width}
				value={[String(value ?? "")]}
				onValueChange={(details) => {
					const nextValue = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
					onValueSelect(nextValue);
				}}
			>
				{label ? <SelectLabel>{label}</SelectLabel> : null}
				<SelectTrigger id={name}>
					<SelectValueText placeholder={placeholder}>
						{(selectedItems) => getSelectLabel(selectedItems[0])}
					</SelectValueText>
				</SelectTrigger>
				<Portal>
					<SelectContent>
						{collection.items.map((item) => (
							<SelectItem key={item.value} item={item}>
								{item.label}
							</SelectItem>
						))}
					</SelectContent>
				</Portal>
			</SelectRoot>
		);

		if (!label) {
			return <Box width="100%">{selectControl}</Box>;
		}

		return <Field.Root style={{ width: "100%" }}>{selectControl}</Field.Root>;
	};

	return (
		<Box width="100%">
			<Box
				display="grid"
				gridTemplateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
				alignItems="start"
				gap="10px"
				marginBottom="24px"
			>
				{renderTextFilterField({
					label: "User",
					name: "keyword",
					value: filters.keyword,
					onChange: handleInputChange,
					placeholder: "Name, email, or user id",
				})}
				{renderTextFilterField({
					label: "From",
					name: "from",
					type: "date",
					value: filters.from,
					onChange: handleInputChange,
				})}
				{renderTextFilterField({
					label: "To",
					name: "to",
					type: "date",
					value: filters.to,
					onChange: handleInputChange,
				})}
				{renderSelect({
					label: "Acting As",
					name: "actingAs",
					collection: actingAsCollection,
					value: filters.actingAs,
					onValueSelect: (nextValue) => handleActingAsChange("actingAs", nextValue),
					placeholder: "All acting users",
				})}
				{renderSelect({
					label: "Module / Page",
					name: "page",
					collection: pageCollection,
					value: filters.page,
					onValueSelect: (nextValue) => handlePageChange("page", nextValue),
					placeholder: "All modules / pages",
				})}
				{renderSelect({
					label: "Login",
					name: "loginFilter",
					collection: loginCollection,
					value: filters.loginFilter,
					onValueSelect: (nextValue) => handleLoginFilterChange("loginFilter", nextValue),
					placeholder: "Both",
				})}
				{renderTextFilterField({
					label: "IP",
					name: "ip",
					value: filters.ip,
					onChange: handleInputChange,
					// Text filters accept simple AND/OR expressions; examples stay in placeholder text.
					placeholder: "10.0.0.1 OR 10.0.0.2",
				})}
				{renderTextFilterField({
					label: "Action",
					name: "event",
					value: filters.event,
					onChange: handleInputChange,
					placeholder: "pageLoaded AND actingAs",
				})}
			</Box>

			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				flexWrap="wrap"
				gap="10px"
				marginBottom="8px"
			>
				<Text textStyle="sm">
					Showing {formatCount(batchStart)}-{formatCount(batchEnd)} of {formatCount(total)} activities
				</Text>
				<Box display="flex" alignItems="center" gap="10px" flexWrap="wrap">
					<Button size="xs" onClick={onPrevBatch} variant="subtle" disabled={loading || offset <= 0}>
						Previous {currentLimit}
					</Button>
					<Button size="xs" onClick={onNextBatch} variant="subtle" disabled={loading || !hasMore}>
						Next {currentLimit}
					</Button>
					<Box minWidth="180px">
						{renderSelect({
							name: "sortBy",
							collection: sortCollection,
							value: currentSortId,
							// Keep the compact toolbar selects aligned with the neighboring xs buttons.
							isCompact: true,
							onValueSelect: (nextValue) => {
								const selectedOption = findSelectOption(sortOptions, nextValue);
								if (selectedOption) handleSortOptionChange(selectedOption);
							},
							placeholder: "Sort By",
						})}
					</Box>
					<Box minWidth="92px">
						{renderSelect({
							name: "rowsPerPage",
							collection: rowsPerPageCollection,
							value: String(currentLimit),
							// Keep the compact toolbar selects aligned with the neighboring xs buttons.
							isCompact: true,
							onValueSelect: (nextValue) =>
								handleRowsPerPageChange({ target: { value: nextValue || "100" } }),
							placeholder: "Rows",
						})}
					</Box>
					<Button size="xs" onClick={clearFilters} variant="subtle">
						Clear
					</Button>
					<Button size="xs" onClick={applyFilters} disabled={loading}>
						Apply Filters
					</Button>
				</Box>
			</Box>

			<UserActivityResults
				items={items}
				order={order}
				orderBy={orderBy}
				onRequestSort={handleRequestSort}
				columns={columns}
			/>
		</Box>
	);
};

export default UserActivitiesList;
